import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CardContainer, ContentContainer, MainContainer, StyledCard } from '../focus_view_shared.styles'
import { AlertContainer, StyledAlert } from './bulk_manage.styles'
import { CardHeader, Text } from '@amzn/storm-ui'
import { StageDiveConfig, StageDiveConfigContext } from '../../../../../util/app_initialization/appconfig'
import { ManageAdvertisersV2 } from './manage_advertisers_v2'
import * as i18nKeyList from './bulk_manage_i18n_mappings'
import { toNullable } from 'fp-ts/es6/Option'
import { useLoginContext } from 'stagedive/LoginContext'
import { getManageAccountsURL } from '../ads_console_utils'
import * as AACChromeAPI from '@amzn/aac-chrome-api'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'

export type UnlinkAdvertisersResult = 'success' | 'error' | null
export const BulkManage: React.FunctionComponent = (): JSX.Element => {
  const stageDiveConfig = useContext<StageDiveConfig>(StageDiveConfigContext)
  const { t } = useTranslation()
  const [unlinkRequestResult, setUnlinkRequestResult] = useState<UnlinkAdvertisersResult>(null)
  const { loginContext } = useLoginContext()
  const partnerAccountId = toNullable(loginContext.partnerProfileId) ?? ''

  useEffect(() => {
    getMetricsPublisherChainAsync('AdsConsoleInt').then((p) => p.publishCounter('view', 1).publishString(`page`, `bulk-manage`))
  }, [])

  /* istanbul ignore next */
  useEffect(() => {
    const config = AACChromeAPI.getChromeConfiguration()
    const pageHeader = new AACChromeAPI.PageHeader()
    const objectInfo = new AACChromeAPI.ObjectInfo()
    const actionList = new AACChromeAPI.ItemList()

    config.setPageLayoutType('FOCUS')
    const focusModeExitEventId = 'ExitFocusView'

    const backToAccountsLink = new AACChromeAPI.Button()
    const backToAccountsLinkButtonId = 'backToAccountsLink'
    backToAccountsLink.setId(backToAccountsLinkButtonId)
    backToAccountsLink.setLabel(t(i18nKeyList.BULK_MANAGE_BACK_TO_ACCOUNTS))
    backToAccountsLink.setButtonType(AACChromeAPI.ButtonType.LINK)

    const backToAccountsLinkFn = (): void => {
      window.location.href = getManageAccountsURL(partnerAccountId, stageDiveConfig.hostname)
    }
    AACChromeAPI.getActionEventTarget().addEventListener(backToAccountsLinkButtonId, backToAccountsLinkFn)
    AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, backToAccountsLinkFn)

    actionList.addItem(backToAccountsLink)

    objectInfo.setActions(actionList)

    pageHeader.setObjectInfo(objectInfo)
    pageHeader.setPageName(t(i18nKeyList.BULK_MANAGE_PAGE_TITLE))
    config.setPageHeader(pageHeader)
    AACChromeAPI.setChromeConfiguration(config)
    return () => {
      AACChromeAPI.getActionEventTarget().removeEventListener(backToAccountsLinkButtonId, backToAccountsLinkFn)
      AACChromeAPI.getCommonEventTarget().removeEventListener(focusModeExitEventId, backToAccountsLinkFn)
    }
  }, [t])

  return (
    <MainContainer>
      <ContentContainer>
        <AlertContainer>
          <StyledAlert
            type={unlinkRequestResult === 'success' ? 'success' : 'error'}
            isOpen={!!unlinkRequestResult}
            withCloseButton={true}
            onClose={() => setUnlinkRequestResult(null)}
          >
            <Text>{unlinkRequestResult === 'success' ? t(i18nKeyList.BULK_MANAGE_SUCCESS_ALERT) : t(i18nKeyList.BULK_MANAGE_ERROR_ALERT)} </Text>
          </StyledAlert>
        </AlertContainer>
        <CardContainer>
          <StyledCard>
            <CardHeader>
              <Text textColor="base" type="h2">
                {t(i18nKeyList.BULK_MANAGE_CARD_TITLE)}
              </Text>
              <Text textColor="secondary" fontSize={'small'}>
                {t(i18nKeyList.BULK_MANAGE_CARD_DESCRIPTION)}
              </Text>
            </CardHeader>
            <ManageAdvertisersV2 setUnlinkRequestResult={setUnlinkRequestResult} unlinkRequestResult={unlinkRequestResult} />
          </StyledCard>
        </CardContainer>
      </ContentContainer>
    </MainContainer>
  )
}
